import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { EntityTypes } from 'constants/entities';
import { reportRequest } from 'core/services/admin';
import { searchRequest } from 'core/services/product';
import { IKeyArray } from 'types/IKeyArray';
import { CourseProgressStatus } from 'types/domain/permissions';
import { VariableType } from 'types/domain/variable';

export interface IReportReducer {
  reports: IKeyArray<IReport>;
  custom: ICustomReport;
}

export interface ICustomReport {
  type: EntityTypes;
  reportType: ReportType;
  filter: IReportFilter;
  componentId?: string;
}

export interface IReportFilter {
  groupId?: number;
  variableId?: number;
  status?: CourseProgressStatus;
  start?: Date;
  end?: Date;
  variableType?: VariableType;
  accessLevel?: number;
  role?: number;
  minRole?: number;
  courseNodeId?: number;
}

export interface ICustomReportUpdate {
  type: EntityTypes;
  id: number;
  reportType?: ReportType;
}

export interface IPageUpdate {
  type: ReportType;
  pageIndex: number;
}

export interface IReport {
  totalRecords: number;
  pageIndex: number;
  loading: IKeyArray<boolean>;
  sort: number;
  selected: IKeyArray<boolean>;
  pageCount: number;
}

export enum ReportType {
  LATEST = 0,
  GROUPS = 1,
  GROUP = 2,
  USERS = 3,
  RESPONSES = 4,
  ATTEMPTS = 5,
  CERTIFICATES = 6,
  CERTIFICATE = 7,
  VARIABLES = 8,
  VARIABLE = 9,
  MANAGE_USERS = 10,
  MANAGE_GROUP_USERS = 11,
  CUSTOM = 12,
  GROUP_USERS = 13,
  COURSE = 14,
  ORG_USER_COURSE = 15,
  COURSE_PROGRESS = 16,
  ORG_USER_CERTIFICATE = 17,
  COURSE_PROGRESS_LOG = 18,
  ORG_USER_VARIABLE = 19,
  /** Where a course's learners are right now, step by step — the workflow map's learner numbers. */
  COURSE_ACTIVITY = 20,
}

const initialState: IReportReducer = {
  reports: {
    [ReportType.COURSE_PROGRESS]: {
      selected: {},
      totalRecords: null,
      pageCount: 10,
      pageIndex: null,
      loading: {},
      sort: null,
    },
  },
  custom: {
    type: null,
    reportType: null,
    filter: {
      groupId: null,
      status: null,
      start: null,
      end: null,
      variableType: null,
      variableId: null,
    },
  },
};

export const reportSlice = createSlice({
  name: 'offline',
  initialState,
  reducers: {
    updatePageIndex(state, action: PayloadAction<IPageUpdate>) {
      if (action.payload.type)
        state.reports[action.payload.type] = {
          ...state.reports[action.payload.type],
          pageIndex: action.payload.pageIndex,
          //loading: {[]}
        };
    },
    updateCustomReport(state, action: PayloadAction<ICustomReport>) {
      state.custom = action.payload;
    },
    selectReportRow(state, action: PayloadAction<{ type: ReportType; id: number; selected: boolean }>) {
      state.reports[action.payload.type].selected[action.payload.id] = action.payload.selected;
    },
    clearReportSelection(state, action: PayloadAction<{ type: ReportType }>) {
      state.reports[action.payload.type].selected = {};
    },
    exportReportData() {},
  },
  extraReducers: builder => {
    builder.addCase(reportRequest.pending, (state, action) => {
      state.reports[action.meta.arg.reportType] = {
        ...state.reports[action.meta.arg.reportType],
        pageCount: action.meta.arg.pagerInfo.pageCount,
        loading: {
          ...(state.reports[action.meta.arg.reportType]?.loading || {}),
          [action.meta.arg.pagerInfo?.page]: true,
        },
      };
    });
    builder.addCase(reportRequest.fulfilled, (state, action) => {
      state.reports[action.meta.arg.reportType] = {
        ...state.reports[action.meta.arg.reportType],
        totalRecords: action.payload.pagerInfo?.totalRecords,
        loading: {
          ...(state.reports[action.meta.arg.reportType]?.loading || {}),
          [action.meta.arg.pagerInfo?.page]: false,
        },
      };
    });

    builder.addCase(searchRequest.fulfilled, (state, action) => {
      if (action.meta.arg.reportType)
        state.reports[action.meta.arg.reportType] = {
          ...state.reports[action.meta.arg.reportType],
          totalRecords: action.payload.pagerInfo?.totalRecords,
          loading: {
            ...(state.reports[action.meta.arg.reportType]?.loading || {}),
            [action.meta.arg.pagerInfo?.page]: false,
          },
        };
    });
  },
});

export const { updatePageIndex, updateCustomReport, exportReportData, selectReportRow, clearReportSelection } =
  reportSlice.actions;

export default reportSlice.reducer;
